import { useEffect, useState, useRef, useMemo } from "react";
import Talk from "talkjs";
import getAvatarUrl from "../../../utils/get-avatar-url";
import moment from "moment";
import submit from "submit-form";
import { parseAthletesOrganizationInfoToClientList } from "../../../utils/user-helpers";

const TALK_JS_APP_ID = "tMXnzHXl";
const TALK_JS_SECRET_KEY = "sk_test_YkD7oUFWR8MLK4AMmnm4hNZxHE7cX0m7";

const getAthleteInfo = (participants, athletes, userId) => {
  const athleteId = Object.keys(participants).find(
    (participantId) => participantId !== userId
  );

  if (!athleteId) return null;

  const athlete = athletes.find((athlete) => athlete._id === athleteId);

  return athlete;
};

const ChatList = ({
  userId,
  athletes = [],
  conversations = [],
  selectedConversation,
  setSelectedConversation,
  mobileChatOpened,
  setMobileChatOpened,
  classNames = "",
  handleLoadMoreConversations,
  hideSeeMoreConversations,
  unreadConversations,
  isAllowToHandleGroupChats,
  allAthletesFromOrganization,
  usersOrganization,
  activeUser,
  webAppTheme,
}) => {
  const activeUserId = activeUser._id;
  const referAsClients = activeUser.referAsClients;
  const [athletesFilter, setAthletesFilter] = useState("");
  const [loadOnlyUnread, setLoadOnlyUnread] = useState(false);
  const conversationsToShow = loadOnlyUnread
    ? conversations.filter((conversation) =>
        unreadConversations.includes(conversation.id)
      )
    : conversations;

  const organizationActiveUsers =
    usersOrganization && usersOrganization.organizationActiveUsers
      ? usersOrganization.organizationActiveUsers
      : [];

  const allChatsParticipants =
    allAthletesFromOrganization && usersOrganization
      ? allAthletesFromOrganization.concat(organizationActiveUsers, [
          activeUser,
        ])
      : athletes.concat([activeUser]);

  //here we are getting an array of ids of the athletes that matchs with the name of them and the word that the user is filtering in the search inpunt
  const filteredAthletes = useMemo(() => {
    const result = !athletesFilter?.length
      ? athletes
      : athletes.filter((athlete) => {
          const name = athlete.name.toLowerCase();
          const filter = athletesFilter.toLowerCase();

          return name.startsWith(filter);
        });

    return result.map((athlete) => athlete._id);
  }, [athletes, athletesFilter]);

  const openNewGroupModal = (e) => {
    e.stopPropagation();
    e.preventDefault();

    const allGroupChats = conversationsToShow
      .filter(({ subject, photoUrl }) => subject && photoUrl)
      .map(({ subject }) => subject);

    submit("/actions/refresh", {
      method: "GET",
      body: {
        "modal[type]": "create-chat-group",
        groupEdited: "",
        allGroupChats: JSON.stringify(allGroupChats),
      },
    });
  };

  const openEditGroupModal = (e, conversation) => {
    e.stopPropagation();
    e.preventDefault();
    const participants = Object.keys(conversation.participants);
    const participantsString = participants.join(",");

    //Here we are building an string array with the id and subject being seperate by @
    const allGroupChats = conversationsToShow
      .filter(({ id, subject, photoUrl }) => id && subject && photoUrl)
      .map(({ id, subject }) => `${id}@${subject}`);

    submit("/actions/refresh", {
      method: "GET",
      body: {
        "modal[type]": "edit-chat-group",
        groupParticipants: participantsString,
        groupSubject: conversation.subject,
        groupImage: conversation.photoUrl,
        groupChatId: conversation.id,
        groupEdited: "",
        allGroupChats: JSON.stringify(allGroupChats),
      },
    });
  };

  return (
    <div
      className={`chats-list-container${
        classNames.length ? ` ${classNames}` : ""
      }`}
    >
      <div className="search-container">
        <div
          className={
            isAllowToHandleGroupChats
              ? "div-search-conversation-input div-search-conversation-group-input-width"
              : "div-search-conversation-input div-search-conversation-input-width"
          }
        >
          <input
            className="input-search-chat-conversations input-search-chat-conversations-width"
            placeholder={`Search ${referAsClients ? "Client" : "Athlete"}`}
            value={athletesFilter}
            type="search"
            onChange={({ target: { value } }) => setAthletesFilter(value)}
          />
        </div>
        {isAllowToHandleGroupChats && (
          <button
            className="add-group-chat"
            onClick={openNewGroupModal}
            data-button-type="primary"
          >
            New group
          </button>
        )}
        <div
          className={loadOnlyUnread ? "load-only-unread" : ""}
          onClick={() => {
            setLoadOnlyUnread(!loadOnlyUnread);
          }}
        >
          <img
            src="/img/filter_chat.svg"
            alt="filter"
            className={webAppTheme === "dark" ? "filter-white" : "filter-black"}
          />
        </div>
      </div>
      <div className="chats-list">
        {conversationsToShow.map((conversation, idx) => {
          const athlete = getAthleteInfo(
            conversation?.participants,
            athletes,
            userId
          );

          const senderObject = allChatsParticipants.find(
            (participant) =>
              (participant?.athleteId || participant?._id) ===
              conversation?.lastMessage?.senderId
          );

          const lastMessage = {
            sender: {
              name: senderObject?.name || senderObject?.athleteName,
              _id: conversation?.lastMessage?.senderId,
            },
            message: conversation?.lastMessage?.text,
          };

          const groupImage = conversation?.photoUrl ?? undefined;
          const groupSubject = conversation?.subject ?? undefined;

          //not show the conversation if it isn't a group chat and the athlete doesn't exists or isn't part of the active user / organization
          if (
            groupSubject === undefined &&
            (!athlete || !filteredAthletes.includes(athlete._id))
          ) {
            return null;
          }
          //if the conversation is a group chat
          if (groupSubject !== undefined) {
            //? if the user is not filtering the conversations we are returning true if the active user is in the conversation
            //: if the user is filtering the conversations in the search input we are returning true if the filtered athletes are in the conversation
            const isUserIncluded = !athletesFilter?.length
              ? conversation?.participants.hasOwnProperty(activeUserId)
              : filteredAthletes.some((athleteId) =>
                  conversation?.participants.hasOwnProperty(athleteId)
                );
            //not show the conversation if the active user or the filtered athletes (depending on the condition) aren't in the conversation
            if (!isUserIncluded || !groupImage) {
              return null;
            }
          }

          const lastMessageDate = moment(conversation.lastMessage?.createdAt);
          const hoursSinceLastMessage = moment().diff(lastMessageDate, "hours");

          const lastMessageString =
            hoursSinceLastMessage > 144
              ? lastMessageDate.format("MMM D")
              : hoursSinceLastMessage > 48
              ? lastMessageDate.format("dddd")
              : hoursSinceLastMessage > 24
              ? lastMessageDate
                  .calendar(null, {
                    lastDay: "[Yesterday]",
                    sameElse: "L",
                  })
                  .replace(/ at.*/, "")
              : lastMessageDate.format("h:mm a");

          return (
            <div
              style={{
                background:
                  selectedConversation === null
                    ? idx === 0
                      ? "var(--light-gray)"
                      : "none"
                    : selectedConversation === conversation.id ||
                      (selectedConversation &&
                        typeof selectedConversation === "object" &&
                        selectedConversation.id === conversation.id)
                    ? "var(--light-gray)"
                    : "none",
              }}
              onClick={() => {
                setSelectedConversation(conversation.id);
                setMobileChatOpened(true);
              }}
              key={idx}
              className={`chat-overview-container border-bottom-chat${
                !(
                  conversations.length > 19 &&
                  !hideSeeMoreConversations &&
                  !loadOnlyUnread
                )
                  ? " no-border"
                  : ""
              }`}
            >
              <div>
                <div className="avatar">
                  <img
                    src={
                      groupImage
                        ? groupImage
                        : getAvatarUrl(athlete, true, webAppTheme)
                    }
                    className="avatar-image"
                  />
                </div>
                <div className="information">
                  <span>{groupSubject ? groupSubject : athlete.name}</span>
                  <span>
                    {groupSubject && `${lastMessage?.sender?.name}:`}{" "}
                    {conversation.lastMessage?.text}
                  </span>
                </div>
              </div>
              <div className="last-message-time-container">
                <div className="notification-list-message-div">
                  <span className="last-message-time">{lastMessageString}</span>

                  {!unreadConversations.includes(selectedConversation) &&
                    unreadConversations.includes(conversation.id) && (
                      <div className="unread-conversation-div"></div>
                    )}
                  {groupSubject && isAllowToHandleGroupChats && (
                    <a
                      onClick={(event) => {
                        openEditGroupModal(event, conversation);
                      }}
                      className={`icon-element icon-edit-group-chat`}
                    >
                      <img
                        className={
                          webAppTheme === "dark"
                            ? "icon filter-white"
                            : "icon filter-black"
                        }
                        src="/img/new-icons/edit-pencil.svg"
                        alt=""
                      />
                    </a>
                  )}
                </div>
              </div>
            </div>
          );
        })}
        {conversations.length > 39 &&
        !hideSeeMoreConversations &&
        !loadOnlyUnread ? (
          <div
            className={"chat-overview-container-see-more"}
            onClick={() => handleLoadMoreConversations()}
          >
            See more conversations
          </div>
        ) : null}
      </div>
    </div>
  );
};

const ChatDisabled = ({ userId, setChatEnabled, referAsClients }) => {
  const enableChat = async () => {
    setChatEnabled(true);
    submit(`/actions/user/enable-chat`, {
      method: "POST",
      body: { chatEnabled: true, userId },
    });
  };

  return (
    <div className="main-disable-chat">
      <img alt="info" src="/img/svg/chat_info.svg" />
      <span className="chat-title">In-App Chat</span>
      <span className="chat-info">
        Chat with your{referAsClients ? " clients" : " athletes"}'s directly on
        the platform. Send direct messages, audio messages, files and much more.
        Keep your personal life (text messages and WhatsApp) separate from your
        business with this feature.
      </span>
      <span className="span-charge clipped">
        There is no extra charge for this
      </span>
      <div
        className="chat-button"
        onClick={() => {
          enableChat();
        }}
      >
        Enable Chat
      </div>
    </div>
  );
};

function MSBChat({ initId }, context) {
  const chatboxEl = useRef();
  const session = useRef(null);
  const chatbox = useRef(null);
  const [talkLoaded, markTalkLoaded] = useState(false);
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [emptyState, setEmptyState] = useState(true);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [mobileChatOpened, setMobileChatOpened] = useState(false);
  const [lastConversationId, setLastConversationId] = useState(null);
  const [hideSeeMoreConversations, setHideSeeMoreConversations] =
    useState(false);
  const [unreadConversations, setUnreadConversations] = useState([]);

  const {
    locals: {
      activeUser,
      activeUserToRequest,
      organizationRoleName,
      query,
      allAthletesFromOrganization,
      usersOrganization,
    },
  } = context;
  const { groupEdited } = query;
  const isOrganizationUserAndAllowToViewAllMessages =
    organizationRoleName === "Admin" ||
    organizationRoleName === "Owner" ||
    organizationRoleName === "Nutritionist";
  const athletesUsers = isOrganizationUserAndAllowToViewAllMessages
    ? parseAthletesOrganizationInfoToClientList(allAthletesFromOrganization)
    : activeUser.athletes;
  const isRegularCoach =
    !activeUser.organizationId &&
    activeUser.permissions &&
    activeUser.permissions.includes("coach");
  const isOrganizationMember =
    organizationRoleName &&
    (organizationRoleName === "Owner" ||
      organizationRoleName === "Admin" ||
      organizationRoleName === "Nutritionist" ||
      organizationRoleName === "Coach");
  const referAsClients = activeUser.referAsClients;
  const isAllowToHandleGroupChats = isRegularCoach || isOrganizationMember;

  const [chatEnabled, setChatEnabled] = useState(
    activeUser?.chatEnabled || false
  );

  const { _id: userId, name: name, webAppTheme = "dark" } = activeUser;

  if (process.browser) {
    Talk.ready.then(() => markTalkLoaded(true));
  }

  //We are using this filter to avoid the conversations related with PUSH (those PUSH conversations are for the community chat)
  let onlyPullFilter = {
    custom: {},
  };
  const { pushAccountInfo: isFe } = activeUserToRequest;
  const userCanAccessToCommunityChat = isFe?.inAppCommunityEnabled;
  if (!userCanAccessToCommunityChat) {
    onlyPullFilter = {
      custom: { isPush: ["!=", "true"] },
    };
  }
  const encodedOnlyPullFilter = encodeURIComponent(
    JSON.stringify(onlyPullFilter)
  );

  const getInitConversation = async (initAthleteId, conversationList) => {
    const conversation = conversationList.find(
      (conv) => conv.participants[initAthleteId] !== undefined && !conv.subject
    );

    if (!conversation) {
      let talkJSEndpoint = `https://api.talkjs.com/v1/${TALK_JS_APP_ID}/users/${initAthleteId}/conversations?filter=${encodedOnlyPullFilter}`;
      const athleteConversations = await fetch(talkJSEndpoint, {
        headers: {
          Authorization: `Bearer ${TALK_JS_SECRET_KEY}`,
        },
      })
        .then((res) => {
          setLoading(false);
          return res.json();
        })
        .catch(() => {
          setLoading(false);
          return null;
        });

      const conversation = athleteConversations.data.find(
        (conv) => conv.participants[userId] !== undefined && !conv.subject
      );
      return conversation.id;
    } else return conversation.id;
  };

  const restartConversationsState = () => {
    setConversations([]);
    setSelectedConversation(null);
    setLastConversationId(null);
    setEmptyState(true);
  };

  const getConversations = async (reload = false, unreaded = false) => {
    if (!process.browser) {
      return;
    }
    if (unreaded) {
      setLastConversationId(null);
    }

    let talkJSEndpoint = `https://api.talkjs.com/v1/${TALK_JS_APP_ID}/users/${userId}/conversations/?filter=${encodedOnlyPullFilter}&limit=30`;

    if (lastConversationId) {
      talkJSEndpoint += `&startingAfter=${encodeURIComponent(lastConversationId)}`;
    }
    if (unreaded) {
      talkJSEndpoint += `&unreadsOnly=true`;
    }

    if (reload && groupEdited) {
      talkJSEndpoint = `https://api.talkjs.com/v1/${TALK_JS_APP_ID}/users/${userId}/conversations/?filter=${encodedOnlyPullFilter}&limit=30`;
    }

    const conversations = await fetch(talkJSEndpoint, {
      headers: {
        Authorization: `Bearer ${TALK_JS_SECRET_KEY}`,
      },
    })
      .then((res) => {
        setLoading(false);
        return res.json();
      })
      .catch(() => {
        setLoading(false);
        return null;
      });

    if (reload && unreaded) {
      setConversations(conversations.data);
      setSelectedConversation(
        conversations.data?.length
          ? initId
            ? getInitConversation(initId, conversations.data)
            : conversations.data[0].id
          : null
      );
      if (conversations.data?.length < 40) {
        setHideSeeMoreConversations(true);
        return;
      }

      setLastConversationId(
        conversations.data[conversations.data.length - 1].id
      );
      return;
    }

    if (!conversations?.data) {
      if (unreaded) {
        restartConversationsState();
      }
      return;
    }
    setConversations((prevConversations) => {
      if (reload) {
        return conversations.data;
      }
      return [...prevConversations, ...conversations.data];
    });

    if (conversations.data.length) {
      setEmptyState(false);
      if (!selectedConversation) {
        const getValidConversation = (idx = 0) => {
          if (idx >= conversations.data.length) {
            return null;
          }

          const athlete = getAthleteInfo(
            conversations.data[idx].participants,
            athletesUsers,
            userId
          );

          //if it's a group, select it
          if (
            conversations.data[idx].subject &&
            !conversations.data[idx].custom?.isPush
          ) {
            return conversations.data[idx];
          }
          if (!athlete || athletesUsers.includes(athlete._id)) {
            return getValidConversation(idx + 1);
          }
          return conversations.data[idx];
        };

        const conversation = initId
          ? await getInitConversation(initId, conversations.data)
          : getValidConversation();

        if (!conversation) {
          setEmptyState(true);
          return;
        }
        setSelectedConversation(conversation);
      }

      if (conversations.data.length < 40) {
        if (unreaded) {
          restartConversationsState();
        }
        if (reload) {
          setLastConversationId(null);
        }
        setHideSeeMoreConversations(true);
        return;
      }

      if (hideSeeMoreConversations) {
        setHideSeeMoreConversations(false);
      }

      setLastConversationId(
        conversations.data[conversations.data.length - 1].id
      );
    }
  };

  const loadChat = async () => {
    let actualSession = null;
    if (!session.current) {
      const currentUser = await new Talk.User({
        id: userId,
        name,
        role: webAppTheme === "dark" ? "msbdarktheme" : "msbwhitetheme",
      });

      actualSession = await new Talk.Session({
        appId: TALK_JS_APP_ID,
        me: currentUser,
      });

      session.current = actualSession;
      chatbox.current = await actualSession.createChatbox(
        {
          messageField: { spellcheck: true },
        },
        {
          selected: selectedConversation || null,
        },
        { showLocationButton: false }
      );
      await chatbox.current.mount(chatboxEl.current);
      chatbox.current.select(
        typeof selectedConversation === "string"
          ? selectedConversation
          : selectedConversation?.id || null
      );

      if (initId) chatbox.current.select(selectedConversation);
      if (initId && window.innerWidth < 960) setMobileChatOpened(true);

      actualSession.unreads.onChange((unreadConversations) => {
        const conversationsIds = unreadConversations.map(
          (item) => item.lastMessage.conversation.id
        );
        setUnreadConversations(conversationsIds);
      });
      actualSession.onMessage(() => {
        getConversations(true);
      });
      getConversations(true);
    } else {
      chatbox.current.select(selectedConversation);
    }
  };

  const resetChat = () => {
    setSelectedConversation(null);
    setLastConversationId(null);
    session.current?.destroy();
    session.current = null;
    chatbox.current = null;
    loadChat();
  };

  useEffect(() => {
    if (!talkLoaded || !selectedConversation) return;

    loadChat();
  }, [talkLoaded, selectedConversation]);

  /**
   * useEffect to reload the chat with the correct theme, if the user changes it from the left bar
   * (if the user changes it when the chat is opened the page is going to reload with the groupEdited query)
   * Because with that query is easier to handle the conversations, all the logic is already done for that.
   */
  useEffect(() => {
    if (talkLoaded && !!session.current && groupEdited) {
      resetChat();
    }
  }, [webAppTheme]);

  useEffect(() => {
    if (groupEdited) {
      resetChat();
    }
  }, [groupEdited]);

  useEffect(() => {
    // Hide the Intercom Chat since it overlaps the chat buttons
    window.Intercom &&
      window.Intercom("update", { hide_default_launcher: true });
    window.Intercom && window.Intercom("hide");

    // hide element from dom and adding border in the header
    const arrow = document.querySelector("#profile-dropdown picture");
    const profileBox = document.querySelector("#profile-dropdown summary");
    const options = document.querySelector("#profile-dropdown > div");
    const header = document.querySelector('header[role="main"]');
    header.style.borderBottom = "1px solid var(--white-20)";
    arrow.style.display = "none";
    profileBox.style.cursor = "initial";
    options.style.visibility = "hidden";

    return () => {
      session.current?.destroy();
      // Show again the Intercom Chat
      window.Intercom &&
        window.Intercom("update", { hide_default_launcher: false });

      // hide element from dom, remove the border in the footer and remove the mobile classes
      const arrow = document.querySelector("#profile-dropdown picture");
      const options = document.querySelector("#profile-dropdown > div");
      const pageWrapper = document.getElementById("page-wrapper");
      const coachingWrapper = document.getElementById("coaching-wrapper");
      const bodyClass = document.body;
      arrow.style.display = "block";
      options.style.visibility = "unset";
      profileBox.style.cursor = "pointer";
      header.style.borderBottom = "";
      removeMobileClasses(pageWrapper, coachingWrapper, bodyClass);
    };
  }, []);

  useEffect(() => {
    if (!chatEnabled) return;
    getConversations();
  }, [chatEnabled]);

  const handleLoadMoreConversations = async (unreaded = false) => {
    if (groupEdited) {
      submit("/actions/refresh", {
        method: "GET",
        body: {
          groupEdited: "",
        },
      });
    }
    getConversations(false, unreaded);
  };

  const [chatsListClassNames, setChatsListClassNames] = useState(
    "mobile-chat-list-shown"
  );
  const [conversationClassNames, setConversationClassNames] = useState(
    "mobile-conversation-hidden"
  );

  useEffect(() => {
    const pageWrapper = document.getElementById("page-wrapper");
    const coachingWrapper = document.getElementById("coaching-wrapper");
    const bodyClass = document.body;

    if (mobileChatOpened) {
      setConversationClassNames((prevClassName) =>
        prevClassName.replace("hidden", "shown")
      );
      setChatsListClassNames((prevClassName) =>
        prevClassName.replace("shown", "hidden")
      );

      addMobileClasses(pageWrapper, coachingWrapper, bodyClass);
    } else {
      removeMobileClasses(pageWrapper, coachingWrapper, bodyClass);
      setConversationClassNames((prevClassName) =>
        prevClassName.replace("shown", "hidden")
      );
      setChatsListClassNames((prevClassName) =>
        prevClassName.replace("hidden", "shown")
      );
    }
  }, [mobileChatOpened]);

  const addMobileClasses = (pageWrapper, coachingWrapper, body) => {
    pageWrapper.classList.add("page-wrapper-mobile-chat-opened");
    coachingWrapper.classList.add("coaching-wrapper-mobile-chat-opened");
  };

  const removeMobileClasses = (pageWrapper, coachingWrapper, body) => {
    pageWrapper.classList.remove("page-wrapper-mobile-chat-opened");
    coachingWrapper.classList.remove("coaching-wrapper-mobile-chat-opened");
  };

  if (!chatEnabled) {
    return (
      <div className="Main Coaching-Chat chat-disabled">
        <ChatDisabled
          userId={userId}
          setChatEnabled={setChatEnabled}
          referAsClients={referAsClients}
        />
      </div>
    );
  }

  if (loading) {
    return (
      <div className="Main Coaching-Chat chat-loading">
        <span>Loading...</span>
      </div>
    );
  }

  if (emptyState) {
    return (
      <div className="Main Coaching-Chat No-Chat">
        <div className="no-conversations">
          <div className="box-container">
            <span className="title">No Chats Yet!</span>
            <p className="info">
              No conversations available. Go to the
              {referAsClients ? " clients " : " athletes "}List to add your
              first{referAsClients ? " client" : " athlete"}.
            </p>
            <a
              type="button"
              className="button save"
              href="/coaching-portal/athletes"
            >
              Go to{referAsClients ? " clients " : " athletes "}List
            </a>
          </div>
        </div>
      </div>
    );
  }

  return (
    <main
      className={
        mobileChatOpened
          ? "Main Coaching-Chat main-mobile-chat-opened"
          : "Main Coaching-Chat main-mobile-chat-closed"
      }
    >
      <>
        <ChatList
          conversations={conversations}
          athletes={athletesUsers}
          userId={userId}
          selectedConversation={selectedConversation}
          setSelectedConversation={setSelectedConversation}
          mobileChatOpened={mobileChatOpened}
          setMobileChatOpened={setMobileChatOpened}
          classNames={chatsListClassNames}
          handleLoadMoreConversations={handleLoadMoreConversations}
          hideSeeMoreConversations={hideSeeMoreConversations}
          unreadConversations={unreadConversations}
          isAllowToHandleGroupChats={isAllowToHandleGroupChats}
          allAthletesFromOrganization={allAthletesFromOrganization}
          usersOrganization={usersOrganization}
          activeUser={activeUser}
          webAppTheme={webAppTheme}
        />
        <div
          className={
            webAppTheme == "dark"
              ? `dark-conversation-container conversation-container ${conversationClassNames}`
              : `light-conversation-container conversation-container ${conversationClassNames}`
          }
        >
          <div
            className="only-show-mobile chat-back"
            onClick={() => setMobileChatOpened(false)}
          >
            <img
              src="/img/new-icons/arrow-square-left.svg"
              className={
                webAppTheme === "dark" ? "filter-white" : "filter-black"
              }
            />
            <span>Back</span>
          </div>

          <div
            className={
              mobileChatOpened
                ? "chat-box chat-box-mobile-chat-opened"
                : "chat-box"
            }
            ref={chatboxEl}
          >
            <img src="/img/spinner.gif" width="50" height="50" />
          </div>
        </div>
      </>
    </main>
  );
}

export default MSBChat;
